import {
  MoreOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Empty,
  MenuProps,
  Skeleton,
  Space,
  TablePaginationConfig,
} from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { Column, RecordCountBadge, Table } from 'components';
import { StyledTableHeader } from 'components/table/table.styled';
import { TableLoader } from 'components/table-loader/table-loader';
import { RefundViewModal } from 'features/transaction_list/component/refund-modal-view/refund-view-model';
import { useCreateRefundApproveReject } from 'features/transaction_list/use-cases/approve-reject-refund';
import { useUserContext } from 'features/users';
import { EndUserFilterFormValuesPaginated } from 'features/users/components/users-filter-form';
import { Dispatch, SetStateAction, useState } from 'react';
import { User } from 'types';

type ParticipateTableProps = {
  isSocialListLoading: boolean;
  DeliveryList: any;
  refetch: any;
  tableParams: EndUserFilterFormValuesPaginated;
  updateParams: Dispatch<SetStateAction<EndUserFilterFormValuesPaginated>>;
};

export const RefundRequestList = ({
  DeliveryList,
  isSocialListLoading,
  tableParams,
  updateParams,
}: ParticipateTableProps) => {
  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter: SorterResult<User> | SorterResult<User>[],
  ) => {
    updateParams({
      ...tableParams,
      page: pagination.current,
      size: pagination.pageSize,
      order_by: Array.isArray(sorter)
        ? undefined
        : sorter.order === 'ascend'
          ? 'name'
          : '-name',
    });
  };

  const [showModal, setShowModal] = useState(false);
  const [refundedData, setRefundedData] = useState<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { mutate: approveReject, isLoading: loading } = useCreateRefundApproveReject();
  const { user } = useUserContext();
  const handleCloseModal = () => {
    setShowModal(false);
    setRefundedData(null);
    document.body.style.overflow = 'visible';
  };

  const handleRefundAction = (
    records: any | any[],
    action: 'Approve' | 'Reject'
  ) => {
    const recordsArray = Array.isArray(records) ? records : [records];
    const payload = {
      ActionKey: action,
      UserId: user?.user_id,
      ApprovedBy: user?.role,
      RefundBookingId: JSON.stringify(
        recordsArray.map((r) => ({
          BookingId: r?.BookingId,
        }))
      ),
    };
    approveReject(payload, {
      onSuccess: () => {
        setSelectedRowKeys([]); // 👈 Clear selected checkboxes
      },
    });
  };
  const handleRefundSuperAdminAction = (
    records: any | any[],
    action: 'Approve' | 'Reject'
  ) => {
    const recordsArray:any = Array.isArray(selectedItems) ? selectedItems : [selectedItems];
    const payload = {
      ActionKey: action,
      UserId: user?.user_id,
      ApprovedBy: user?.role,
      RefundBookingId: JSON.stringify(
        recordsArray.map((r:any) => ({
          BookingId: r?.BookingId,
        }))
      ),
    };
    approveReject(payload, {
      onSuccess: () => {
        setSelectedRowKeys([]); // 👈 Clear selected checkboxes
      },
    });
  };
  
  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys,
    columnTitle: null, // Hides the "Select All" checkbox in header
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: () => ({
      // Customize checkbox behavior if needed
    }),
  };


  const selectedItems = DeliveryList?.items?.filter((item: any) =>
    selectedRowKeys.includes(item.BookingId)
  );

  const hasPendingItems = selectedItems?.some(
    (item: any) =>
      item.Status !== 'Approved' && item.Status !== 'Reject'
  );

  return (
    <>
      <div className={`cstmrightmdl ${showModal && 'show'}`}>
        <RefundViewModal
          onClose={handleCloseModal}
          isOpen={showModal}
          RefundData={refundedData}
        />
      </div>

      <StyledTableHeader>
        <div style={{ marginTop: '32px', color: 'black' }}>
          <RecordCountBadge
            text={'Refund Count'}
            count={isSocialListLoading ? 0 : DeliveryList?.total || 0}
          />
        </div>
      </StyledTableHeader>

      {selectedRowKeys.length > 0 && hasPendingItems && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '16px 0',
          }}
        >
          {user?.role == "Accountant" &&
            <div>
              <Button
                type="primary"
                disabled={loading}
                style={{ marginRight: 8 }}
                onClick={() =>
                  handleRefundAction(
                    selectedItems.filter(
                      (item: any) =>
                        item.Status !== 'Approved' &&
                        item.Status !== 'Reject'
                    ),
                    'Approve'
                  )
                }
              >
                Approve
              </Button>
              <Button
                danger
                disabled={loading}
                onClick={() =>
                  handleRefundAction(

                    selectedItems.filter(
                      (item: any) =>
                        item.Status !== 'Approved' &&
                        item.Status !== 'Reject'
                    ),
                    'Reject'
                  )
                }
              >
                Reject
              </Button>
            </div>}
          <RecordCountBadge text="Refund Selected" count={selectedRowKeys.length} />
        </div>
      )}
      {selectedRowKeys.length > 0 && user?.role === 'SuperAdmin' &&
        selectedItems.length > 0 &&
        selectedItems.some((item: any) => item.RefundStatus === 'Approved by Accountant') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '16px 0',
            }}
          >
            <div>
              <Button
                type="primary"
                disabled={loading}
                style={{ marginRight: 8 }}
                onClick={() =>
                  handleRefundSuperAdminAction(
                    selectedItems.filter(
                      (item: any) =>
                        item.RefundStatus === 'Approved by Accountant' &&
                        item.Status !== 'Approved' &&
                        item.Status !== 'Reject'
                    ),
                    'Approve'
                  )
                }
              >
                Approve
              </Button>
              <Button
                danger
                disabled={loading}
                onClick={() =>
                  handleRefundSuperAdminAction(
                    selectedItems.filter(
                      (item: any) =>
                        item.RefundStatus === 'Approved by Accountant' &&
                        item.Status !== 'Approved' &&
                        item.Status !== 'Reject'
                    ),
                    'Reject'
                  )
                }
              >
                Reject
              </Button>
            </div>
            <RecordCountBadge text="Refund Selected" count={selectedRowKeys.length} />
          </div>
        )}



      <Table
        dataSource={isSocialListLoading ? [] : DeliveryList?.items}
        className="cstltblwthldr"
        locale={{
          emptyText: isSocialListLoading ? (
            <Skeleton
              active={true}
              paragraph={{
                rows: tableParams.size || 5,
              }}
            />
          ) : (
            <Empty />
          ),
        }}
        loading={
          isSocialListLoading && {
            indicator: (
              <div>
                <TableLoader />
              </div>
            ),
          }
        }
        rowKey="BookingId"
        onChange={handleTableChange}
        pagination={{
          current: tableParams?.page,
          defaultPageSize: tableParams?.size,
          total: DeliveryList?.total,
        }}
        scroll={{ x: 1040 }}
        rowSelection={rowSelection as any}
      >
        <Column title={'User Name'} dataIndex={'UserName'} />
        <Column title={'Event Name'} dataIndex={'EventName'} />
        <Column title={'Phone Number'} dataIndex={'PhoneNumber'} />
        <Column title={'Refund Amount'} dataIndex={'RefundAmount'} />
        <Column title={'Date'} dataIndex={'TransactionDate'} />
        <Column title={'Payment Type'} dataIndex={'PaymentType'} />
        <Column title={'Status'} dataIndex={'RefundStatus'} />
        {user?.role == "Accountant" &&
          <Column<any>
            title="Action"
            render={(record) => {
              const items: MenuProps['items'] =
                record?.Status === 'Approved' || record?.Status === 'Reject'
                  ? [
                    {
                      key: 'view',
                      label: 'View',
                      onClick: () => {
                        setRefundedData(record);
                        setShowModal(true);
                        document.body.style.overflow = 'hidden';
                      },
                    },
                  ]
                  : [
                    {
                      key: 'approve',
                      label: 'Approve',
                      onClick: () => handleRefundAction(record, 'Approve'),
                    },
                    {
                      key: 'reject',
                      label: 'Rejected',
                      onClick: () => handleRefundAction(record, 'Reject'),
                    },
                    {
                      key: 'view',
                      label: 'View',
                      onClick: () => {
                        setRefundedData(record);
                        setShowModal(true);
                        document.body.style.overflow = 'hidden';
                      },
                    },
                  ];

              return (
                <Dropdown menu={{ items }} trigger={['click']} className="pointer">
                  <Space>
                    <MoreOutlined />
                  </Space>
                </Dropdown>
              );
            }}
          />}
        {user?.role === 'SuperAdmin' && (
          <Column<any>
            title="Action"
            render={(record) => {
              const refundStatus = record?.RefundStatus;

              const showOnlyView = refundStatus === 'Approved by SuperAdmin' || record?.Status === 'Reject';

              const items: MenuProps['items'] = showOnlyView
                ? [
                  {
                    key: 'view',
                    label: 'View',
                    onClick: () => {
                      setRefundedData(record);
                      setShowModal(true);
                      document.body.style.overflow = 'hidden';
                    },
                  },
                ]
                : [
                  {
                    key: 'approve',
                    label: 'Approve',
                    onClick: () => handleRefundAction(record, 'Approve'),
                  },
                  {
                    key: 'reject',
                    label: 'Reject',
                    onClick: () => handleRefundAction(record, 'Reject'),
                  },
                  {
                    key: 'view',
                    label: 'View',
                    onClick: () => {
                      setRefundedData(record);
                      setShowModal(true);
                      document.body.style.overflow = 'hidden';
                    },
                  },
                ];

              return (
                <Dropdown menu={{ items }} trigger={['click']} className="pointer">
                  <Space>
                    <MoreOutlined />
                  </Space>
                </Dropdown>
              );
            }}
          />
        )}
      </Table>
    </>
  );
};
